import { useState, useEffect } from "react";
import logoIcon from "../../assets/logo.png";
import { useNavigate, useLocation } from "react-router-dom";
import { Bell } from "lucide-react";


import API from "../../services/api";
import useAutoLogout from "../../hooks/useAutoLogout";

export const palette = {
  violet: "#5C2D91",
  violetMid: "#7B3FB5",
  red: "#C8102E",
  redMid: "#E02040",
  orange: "#E8640A",
  orangeMid: "#F5830A",
  gold: "#C87800",
  green: "#1A7A42",
  faint: "#C4B8DC",
};

export const gradients = {
  violet: "bg-gradient-to-br from-ap-violet to-ap-violetMid shadow-ap-violet",
  red: "bg-gradient-to-br from-ap-red to-ap-redMid shadow-ap-red",
  orange: "bg-gradient-to-br from-ap-orange to-ap-orangeMid shadow-ap-orange",
  gold: "bg-gradient-to-br from-ap-gold to-ap-orange shadow-ap-gold",
  mixed: "bg-gradient-to-br from-ap-violet to-ap-red shadow-ap-violet",
  warm: "bg-gradient-to-br from-ap-gold to-ap-orange shadow-ap-gold",
};

export const accentBars = {
  violet: "bg-gradient-to-r from-ap-violet to-ap-violetMid",
  red: "bg-gradient-to-r from-ap-red to-ap-orange",
  orange: "bg-gradient-to-r from-ap-orange to-ap-violet",
  mixed: "bg-gradient-to-r from-ap-violet via-ap-red to-ap-orange",
  gold: "bg-gradient-to-r from-ap-gold to-ap-orange",
  green: "bg-gradient-to-r from-ap-green to-ap-violet",
};

const statusClasses = {
  active: "bg-ap-greenLight text-ap-green",
  done: "bg-ap-greenLight text-ap-green",
  completed: "bg-ap-greenLight text-ap-green",
  approved: "bg-ap-greenLight text-ap-green",
  progress: "bg-ap-orangeLight text-ap-orange",
  reporting: "bg-ap-orangeLight text-ap-orange",
  review: "bg-ap-violetLight text-ap-violet",
  pending: "bg-ap-goldLight text-ap-gold",
  busy: "bg-ap-goldLight text-ap-gold",
  high: "bg-ap-goldLight text-ap-gold",
  alert: "bg-ap-redLight text-ap-red",
  urgent: "bg-ap-redLight text-ap-red",
  delayed: "bg-ap-redLight text-ap-red",
  inactive: "bg-ap-redLight text-ap-red",
  upcoming: "bg-slate-100 text-slate-400",
};

export function statusClass(key = "") {
  return statusClasses[String(key).toLowerCase().replace(/\s+/g, "")] || "bg-ap-violetLight text-ap-violet";
}

export function StatusBadge({ label, status = label }) {
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-bold ${statusClass(status)}`}>
      <span className="h-1.5 w-1.5 rounded-full bg-current" />
      {label}
    </span>
  );
}

export function Card({ children, bar = "violet", className = "" }) {
  return (
    <section className={`overflow-hidden rounded-[18px] border border-ap-border bg-ap-surface ${className}`}>
      <div className={`h-[3px] ${accentBars[bar] || accentBars.violet}`} />
      {children}
    </section>
  );
}

export function SectionHeader({ title, subtitle, action }) {
  return (
    <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
      <div>
        <h2 className="font-syne text-sm font-bold text-ap-dark">{title}</h2>
        {subtitle && <p className="mt-0.5 text-[11px] text-ap-muted">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

export function PrimaryButton({ children, className = "", onClick, tone = "violet" }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`${gradients[tone] || gradients.violet} rounded-[10px] px-4 py-2 text-xs font-bold text-white transition hover:-translate-y-0.5 hover:opacity-95 ${className}`}
    >
      {children}
    </button>
  );
}

export function GhostButton({ children, active, onClick, tone = "violet", className = "" }) {
  const activeClass = {
    violet: "border-ap-violet bg-ap-violetLight text-ap-violet",
    orange: "border-ap-orange bg-ap-orangeLight text-ap-orange",
    red: "border-ap-red bg-ap-redLight text-ap-red",
    gold: "border-ap-gold bg-ap-goldLight text-ap-gold",
  }[tone];
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-full border px-3 py-1.5 text-[11px] font-semibold transition ${
        active ? activeClass : "border-ap-border bg-transparent text-ap-muted hover:bg-ap-violetLight hover:text-ap-violet"
      } ${className}`}
    >
      {children}
    </button>
  );
}

export function KpiCard({ label, value, icon, badge, tone = "violet", onClick }) {
  return (
    <div
      onClick={onClick}
      role={onClick ? "button" : undefined}
      className={`${gradients[tone] || gradients.violet} group relative min-h-[132px] overflow-hidden rounded-[18px] p-5 text-white transition hover:-translate-y-1 ${onClick ? "cursor-pointer" : ""}`}
    >
      <div className="absolute -right-5 -top-5 h-[76px] w-[76px] rounded-full bg-white/10" />
      <div className="absolute -bottom-6 right-2 h-[54px] w-[54px] rounded-full bg-white/10" />
      {badge && <span className="absolute right-3.5 top-3.5 rounded-full bg-white/20 px-2 py-0.5 text-[9px] font-bold">{badge}</span>}
      <div className="mb-3 text-xl">{icon}</div>
      <div className="font-syne text-3xl font-extrabold leading-none">{value}</div>
      <div className="mt-1 text-xs text-white/80">{label}</div>
    </div>
  );
}

export function ProgressBar({ value, tone = "violet" }) {
  const fill = {
    violet: "bg-ap-violet",
    red: "bg-ap-red",
    orange: "bg-ap-orange",
    gold: "bg-ap-gold",
    green: "bg-ap-green",
  }[tone] || "bg-ap-violet";
  const width = {
    0: "w-0", 30: "w-[30%]", 45: "w-[45%]", 55: "w-[55%]", 65: "w-[65%]", 68: "w-[68%]",
    70: "w-[70%]", 74: "w-[74%]", 78: "w-[78%]", 82: "w-[82%]", 90: "w-[90%]", 100: "w-full",
  }[value] || "w-2/3";
  return (
    <div className="h-1.5 overflow-hidden rounded-full bg-ap-violetLight">
      <div className={`h-full rounded-full ${fill} ${width}`} />
    </div>
  );
}

export function Avatar({ initials, tone = "violet", size = "md" }) {
  const sizeClass = size === "sm" ? "h-6 w-6 rounded-[7px] text-[9px]" : size === "lg" ? "h-10 w-10 rounded-xl text-sm" : "h-9 w-9 rounded-[10px] text-[11px]";
  return (
    <div className={`${sizeClass} ${gradients[tone] || gradients.violet} flex shrink-0 items-center justify-center font-syne font-extrabold text-white`}>
      {initials}
    </div>
  );
}

export function DashboardShell({ portal, roleLabel, title, subtitle, navItems, user, children, homePath }) {
const navigate = useNavigate();
  const location = useLocation();

  // Treat "/project-add?edit_id=..." as "/project-list" for sidebar highlighting,
  // since editing an existing project is conceptually part of Project List, not Add Project.
  const searchParams = new URLSearchParams(location.search);
  const effectivePath =
    location.pathname === "/project-add" && searchParams.get("edit_id")
      ? "/project-list"
      : location.pathname;

const handleLogout = () => {
  localStorage.clear();
  sessionStorage.clear();
  navigate("/");
};

  // Auto-logout after 5 hours of inactivity, or after being away from this
  // tab (switched tabs, minimized, moved to another app) for 5 hours.
  // Reuses the exact same handleLogout as the manual Logout button above,
  // so it clears the same storage and redirects the same way.
  useAutoLogout(handleLogout, 10 * 60 * 1000); // 10 hours -  10 * 60 * 60 * 1000

  const [mobileOpen, setMobileOpen] = useState(false);
  const [localActive, setLocalActive] = useState(navItems?.[0]?.key || homePath);
  const [openMenu, setOpenMenu] = useState(null);
  const [notifications, setNotifications] = useState([]);
const [unreadCount, setUnreadCount] = useState(0);
const [showNotifications, setShowNotifications] = useState(false);
useEffect(() => {
  fetchNotifications();
  fetchUnreadCount();
}, []);
const handleNotificationClick = async (notification) => {
  try {
    if (notification.is_read === 0) {
      await API.put(`/notifications/${notification.id}/read`);

      setNotifications((prev) =>
        prev.map((n) =>
          n.id === notification.id
            ? { ...n, is_read: 1 }
            : n
        )
      );

      setUnreadCount((prev) => Math.max(0, prev - 1));
    }

    setShowNotifications(false);

    switch (notification.type) {
      case "project_assigned":
  navigate("/engineer/projects");
  break;

      case "project_approved":
        navigate("/project-list");
        break;

      case "report_pending":
        navigate("/report-approval");
        break;

      case "report_approved":
        navigate("/project-reports");
        break;

      default:
        break;
    }
  } catch (err) {
    console.error(err);
  }
};

const fetchNotifications = async () => {
  try {
    const res = await API.get("/notifications");

    if (res.data.success) {
      setNotifications(res.data.data || []);
    }
  } catch (err) {
    console.error("Notification Error:", err);
  }
};

const fetchUnreadCount = async () => {
  try {
    const res = await API.get("/notifications/unread-count");

    if (res.data.success) {
      setUnreadCount(res.data.count || 0);
    }
  } catch (err) {
    console.error("Unread Count Error:", err);
  }
};

  const activate = (item) => {
    if (item.children?.length) {
      setOpenMenu((current) => (current === item.label ? null : item.label));
      return;
    }
    setOpenMenu(null);
    setLocalActive(item.key || item.path);
    setMobileOpen(false);
    if (item.path) navigate(item.path);
  };

const activeKey = (item) => {
    if (item.path) return effectivePath === item.path;
    if (item.children?.some((child) => child.path === effectivePath)) return true;
    return item.key ? localActive === item.key : false;
  };
const SidebarBody = () => (
    <>
      <div className="mb-7 flex items-center gap-3 px-2.5 py-1">
       <div className="h-10 w-10 rounded-xl bg-white flex items-center justify-center flex-shrink-0 p-1">
          <img
            src={logoIcon}
            alt="Asian Paints"
            className="h-full w-full object-contain"
          />
        </div>
        <div className="min-w-0">
          <div className="truncate font-syne text-[18px] font-extrabold leading-tight text-[#F2EAFB]">Asian Paints</div>
          <div className="mt-0.5 text-xs text-ap-muted">{portal}</div>
        </div>
      </div>
      <div className="px-2.5 pb-2 text-[10px] font-bold uppercase tracking-widest text-ap-faint">Main Menu</div>
      <div className="no-scrollbar flex-1 space-y-1 overflow-y-auto overflow-x-hidden pr-0.5">
        {navItems.map((item) => {
          const isActive = activeKey(item);
          const isOpen = item.children?.length > 0 && (openMenu === item.label || (openMenu === null && isActive));
          return (
            <div key={item.path || item.key || item.label}>
              <button
                type="button"
                onClick={() => activate(item)}
                className={`flex w-full items-center gap-3 rounded-[12px] border-l-[3px] px-3.5 py-3 text-left transition ${
                  isActive
                    ? "border-ap-violetMid bg-ap-sidebarAct text-[#E8DEF8]"
                    : "border-transparent text-ap-muted hover:bg-ap-sidebarHov hover:text-[#E8DEF8]"
                }`}
              >
                <span className="flex h-5 w-5 shrink-0 items-center justify-center text-lg leading-none">{item.icon}</span>
                <span className="min-w-0 flex-1 truncate text-sm font-semibold">{item.label}</span>
                {item.children?.length > 0 && (
                  <span className={`text-[10px] transition ${isOpen ? "rotate-90" : ""}`}>›</span>
                )}
                {item.badge && <span className="h-1.5 w-1.5 rounded-full bg-ap-red" />}
              </button>

              {item.children?.length > 0 && isOpen && (
                <div className="ml-6 mt-1.5 space-y-1 border-l border-white/10 pl-2.5">
{item.children.map((child) => {
                    const childActive = effectivePath === child.path;
                    return (
                      <button
                        key={child.path || child.label}
                        type="button"
                        onClick={() => activate(child)}
                          className={`flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-xs font-semibold transition ${
                          childActive
                            ? "bg-ap-sidebarAct text-[#E8DEF8]"
                            : "text-ap-muted hover:bg-ap-sidebarHov hover:text-[#E8DEF8]"
                        }`}
                      >
                        <span className="h-1 w-1 rounded-full bg-current" />
                        <span className="truncate">{child.label}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
     
     
      <div className="mt-4 rounded-[14px] bg-ap-sidebarHov p-3">
          <div className="flex items-center gap-2.5">
            <Avatar initials={user.initials} tone="warm" size="sm" />

            <div className="min-w-0 flex-1">
              <div className="truncate text-xs font-semibold text-[#E8DEF8]">
                {user.name}
              </div>

              <div className="text-[10px] text-ap-muted">
                {user.role}
              </div>

              <button
  onClick={handleLogout}
  className="mt-2 text-[10px] font-semibold text-red-400 hover:text-red-300"
>
  Logout
</button>
            </div>
          </div>
        </div>
    </>
  );

 return (
    <>
    <style>{`@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700;800;900&display=swap'); * { font-family: 'Montserrat', sans-serif !important; }`}</style>
    <div className="min-h-screen bg-ap-bg font-sans text-ap-dark">
      {mobileOpen && <button type="button" aria-label="Close menu" onClick={() => setMobileOpen(false)} className="fixed inset-0 z-40 bg-black/45 backdrop-blur-sm lg:hidden" />}
      <aside className={`no-scrollbar fixed inset-y-0 left-0 z-50 flex w-[260px] flex-col overflow-y-auto overflow-x-hidden border-r border-white/10 bg-ap-sidebar px-5 py-7 shadow-xl transition-transform lg:translate-x-0 ${mobileOpen ? "translate-x-0" : "-translate-x-full"}`}>
        <SidebarBody />
      </aside>
      <div className="flex min-h-screen flex-col lg:pl-[260px]">
        <header className="sticky top-0 z-30 border-b border-ap-border bg-ap-surface px-4 py-3 shadow-sm sm:px-6 lg:px-7">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-3">
              <button type="button" onClick={() => setMobileOpen(true)} className="flex h-9 w-9 items-center justify-center rounded-[10px] border border-ap-border bg-ap-surface text-lg lg:hidden">☰</button>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h1 className="font-syne text-xl font-extrabold leading-tight text-ap-dark sm:text-2xl">{title}</h1>
                  <span className="rounded-full border border-ap-violet/20 bg-ap-violetLight px-2.5 py-1 text-[10px] font-extrabold tracking-wider text-ap-violet">{roleLabel}</span>
                </div>
                <p className="mt-1 text-xs text-ap-muted sm:text-[13px]">{subtitle}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
             
              <div className="relative">
  <button
    type="button"
    onClick={() => setShowNotifications(!showNotifications)}
    className="relative flex h-9 w-9 items-center justify-center rounded-[10px] border border-ap-border bg-ap-surface text-base"
  >
    <Bell size={18} strokeWidth={2} />

    {unreadCount > 0 && (
      <span className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-600 text-[10px] font-bold text-white">
        {unreadCount}
      </span>
    )}
  </button>

  {showNotifications && (
    <div className="absolute right-0 top-12 z-50 w-96 rounded-xl border border-ap-border bg-white shadow-xl">
      <div className="border-b p-3 font-bold">
        Notifications
      </div>

      <div className="max-h-96 overflow-y-auto">
        {notifications.length === 0 ? (
          <div className="p-4 text-center text-sm text-gray-500">
            No Notifications
          </div>
        ) : (
        notifications.map((n) => (
  <div
    key={n.id}
    onClick={() => handleNotificationClick(n)}
    className={`cursor-pointer border-b p-3 ${
      n.is_read === 0 ? "bg-red-50" : ""
    }`}
  >
              <div className="font-semibold">
                {n.title}
              </div>

              <div className="text-sm text-gray-600">
                {n.message}
              </div>

              <div className="mt-1 text-xs text-gray-400">
                {new Date(n.created_at).toLocaleString()}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  )}
</div>
             
            </div>
          </div>
        </header>
     <main className="flex-1 px-4 py-5 sm:px-6 lg:px-7 lg:py-6">{children}</main>
      </div>
    </div>
    </>
  );
}